import { useCrosswordStore } from '../store'

export type TagFilterState = {
  include: string[]
  exclude: string[]
  // When true, exclude any pair whose tags intersect with tags on currently
  // placed pairings — useful late-stage to avoid theme repeats.
  excludeUsedThemes: boolean
}

export const DEFAULT_TAG_FILTER_STATE: TagFilterState = {
  include: [],
  exclude: [],
  excludeUsedThemes: false,
}

/**
 * Apply tag-filter rules to a single pair's tags. Pure helper consumed by
 * every surface that filters pair candidates (sidebar list, cell suggestions,
 * solution search etc).
 *
 * Semantics:
 * - include is OR — at least one of the included tags must be on the pair
 *   (when include is empty, this constraint is skipped)
 * - exclude is OR — none of the excluded tags may be on the pair
 * - usedTagsInGrid: when excludeUsedThemes is on, none of these may appear
 *   on the pair either
 *
 * Pair tags can be undefined; we treat that as "no tags" — a pair with no
 * tags is invisible to include filters (since it matches no tag) and silent
 * to exclude filters (since it shares no tag).
 */
export function passesTagFilter(
  pairTags: ReadonlyArray<string> | undefined,
  filter: TagFilterState,
  usedTagsInGrid: ReadonlySet<string>,
): boolean {
  const tags = pairTags ?? []
  if (filter.include.length > 0) {
    if (!filter.include.some(t => tags.includes(t))) return false
  }
  if (filter.exclude.length > 0) {
    if (filter.exclude.some(t => tags.includes(t))) return false
  }
  if (filter.excludeUsedThemes && usedTagsInGrid.size > 0) {
    if (tags.some(t => usedTagsInGrid.has(t))) return false
  }
  return true
}

type TagFilterRowProps = {
  state: TagFilterState
  onChange: (next: TagFilterState) => void
}

/**
 * Tag filter row for the construct context strip. Each registered tag is a
 * chip that cycles through three states on click:
 *   neutral  → include (blue, solid)
 *   include  → exclude (red, strikethrough)
 *   exclude  → neutral
 *
 * Plus a separate "no theme repeats" chip on the right that excludes any pair
 * sharing a tag with anything currently placed on the grid.
 */
export function TagFilterRow({ state, onChange }: TagFilterRowProps) {
  const tagRegistry = useCrosswordStore(s => s.tags)
  const pairings = useCrosswordStore(s => s.pairings)

  // Only surface tags that are actually attached to a pairing somewhere.
  // Tags in the registry without any user can stay registered (so they show
  // up in autocomplete) but they shouldn't clutter the filter row.
  const tagsInUse = (() => {
    const seen = new Set<string>()
    for (const p of pairings) for (const t of p.tags ?? []) seen.add(t)
    // Preserve registry order.
    return tagRegistry.filter(t => seen.has(t))
  })()

  if (tagsInUse.length === 0 && !state.excludeUsedThemes) {
    return null
  }

  const cycle = (tag: string) => {
    if (state.include.includes(tag)) {
      onChange({
        include: state.include.filter(t => t !== tag),
        exclude: [...state.exclude, tag],
        excludeUsedThemes: state.excludeUsedThemes,
      })
    } else if (state.exclude.includes(tag)) {
      onChange({
        include: state.include,
        exclude: state.exclude.filter(t => t !== tag),
        excludeUsedThemes: state.excludeUsedThemes,
      })
    } else {
      onChange({
        include: [...state.include, tag],
        exclude: state.exclude,
        excludeUsedThemes: state.excludeUsedThemes,
      })
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mr-1">
        tags
      </span>
      {tagsInUse.map(tag => {
        const isInclude = state.include.includes(tag)
        const isExclude = state.exclude.includes(tag)
        const tone = isInclude
          ? 'bg-blue-600 text-white border-blue-600'
          : isExclude
            ? 'bg-red-50 text-red-600 border-red-300 line-through'
            : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400 hover:text-gray-900'
        return (
          <button
            key={tag}
            onClick={() => cycle(tag)}
            className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${tone}`}
            title={
              isInclude
                ? `Include only pairs tagged "${tag}" (click to exclude)`
                : isExclude
                  ? `Hide pairs tagged "${tag}" (click to clear)`
                  : `Click to include only "${tag}"`
            }
          >
            {tag}
          </button>
        )
      })}

      <div className="w-px h-4 bg-gray-200 mx-1" />

      <button
        onClick={() => onChange({ ...state, excludeUsedThemes: !state.excludeUsedThemes })}
        className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
          state.excludeUsedThemes
            ? 'bg-amber-100 text-amber-800 border-amber-300'
            : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
        }`}
        title="Hide pairs that share a tag with anything currently placed on the grid"
      >
        no theme repeats
      </button>
    </div>
  )
}
